export type ScrubDirection = -1 | 0 | 1;

export interface PlaybackState {
  playing: boolean;
  playheadMs: number;
  speed: number;
  /** Non-zero while a scrub button is held. */
  scrub: ScrubDirection;
  /** Wall-clock ms left to hold the playhead after a scene skip. */
  skipHoldMs?: number;
}

export const PLAYBACK_SPEEDS = [0.25, 0.5, 1, 2, 4, 8, 16] as const;
/** Real-time multiplier at ×1 transport speed (motion + scene timing). */
export const TIMELINE_PLAYBACK_BASE = 5;
/** Hold-to-scrub rate relative to normal playback at the selected speed. */
export const SCRUB_RATE = 4;

/** Story playhead for debug overlay (sub-second, with raw ms). */
export function formatPlaybackPlayheadMs(playheadMs: number): string {
  const clamped = Math.max(0, Math.round(playheadMs));
  const minutes = Math.floor(clamped / 60_000);
  const secMs = clamped % 60_000;
  const wholeSec = Math.floor(secMs / 1000);
  const millis = secMs % 1000;
  const clock = minutes > 0
    ? `${minutes}:${wholeSec.toString().padStart(2, "0")}.${millis
      .toString()
      .padStart(3, "0")}`
    : `${wholeSec}.${millis.toString().padStart(3, "0")}s`;
  return `${clock} · ${Math.round(clamped)} ms`;
}

export function createPlayback(
  playheadMs = 0,
  speed: number = 1,
): PlaybackState {
  const clampedSpeed = PLAYBACK_SPEEDS.includes(
    speed as (typeof PLAYBACK_SPEEDS)[number],
  )
    ? speed
    : 1;
  return {
    playing: false,
    playheadMs: Math.max(0, playheadMs),
    speed: clampedSpeed,
    scrub: 0,
  };
}

export interface PlaybackBounds {
  startMs: number;
  endMs: number;
}

export function tickPlayback(
  state: PlaybackState,
  deltaMs: number,
  bounds: number | PlaybackBounds,
  repeat = false,
): PlaybackState {
  const window = boundsOf(bounds);
  const rate = state.speed * TIMELINE_PLAYBACK_BASE;
  if (state.scrub !== 0) {
    const playheadMs = clampPlayhead(
      state.playheadMs + deltaMs * rate * SCRUB_RATE * state.scrub,
      window,
    );
    return { ...state, playheadMs, skipHoldMs: 0 };
  }
  const skipHoldMs = state.skipHoldMs ?? 0;
  if (skipHoldMs > 0) {
    if (deltaMs <= skipHoldMs) {
      return { ...state, skipHoldMs: skipHoldMs - deltaMs };
    }
    return tickPlayback(
      { ...state, skipHoldMs: 0 },
      deltaMs - skipHoldMs,
      bounds,
      repeat,
    );
  }
  if (!state.playing) {
    return state;
  }
  const playheadMs = state.playheadMs + deltaMs * rate;
  if (playheadMs >= window.endMs) {
    if (repeat && window.endMs > window.startMs) {
      return { ...state, playing: true, playheadMs: window.startMs };
    }
    return { ...state, playing: false, playheadMs: window.endMs };
  }
  return { ...state, playheadMs: clampPlayhead(playheadMs, window) };
}

export function playheadInsideSpan(
  playheadMs: number,
  bounds: PlaybackBounds,
): boolean {
  return playheadMs >= bounds.startMs && playheadMs <= bounds.endMs;
}

export function formatSpeedMultiplier(speed: number): string {
  return `×${speed}`;
}

function boundsOf(value: number | PlaybackBounds): PlaybackBounds {
  if (typeof value === "number") {
    return { startMs: 0, endMs: value };
  }
  return value;
}

function clampPlayhead(playheadMs: number, bounds: PlaybackBounds): number {
  return Math.min(bounds.endMs, Math.max(bounds.startMs, playheadMs));
}

export { SCENE_SKIP_HOLD_MS, skipScenePlayhead } from "./PlaybackSkip/skip";

export function activeBeatIndex(
  beats: Array<{ startMs: number; endMs: number }>,
  playheadMs: number,
): number {
  for (let i = 0; i < beats.length; i++) {
    const beat = beats[i];
    if (playheadMs >= beat.startMs && playheadMs < beat.endMs) {
      return i;
    }
  }
  if (beats.length > 0 && playheadMs >= beats[beats.length - 1].endMs) {
    return beats.length - 1;
  }
  return -1;
}
